import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Save, XCircle } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import ReleaseTaskExport from './ReleaseTaskExport.tsx';
import type { ReleaseTask } from './ReleaseTasksPage.tsx';
import {
  createReleaseCompareRowsByBatch,
  createReleaseTask,
  deleteReleaseCompareRowsByBatch,
  getReleaseCompareRowsByBatch,
  getReleaseTasks,
  updateReleaseTask,
  type CompareRowDto,
} from '../../../services/coaReleaseService.ts';
import {
  convertTestValueRawToUnit,
  evaluateTestValueAgainstSpec,
  normalizeComparableText,
  toNumber,
} from './releaseUtils.ts';

export interface ComparisonResult {
  batchNumber: string;
  parameter: string;
  testMethod: string;
  uom: string;
  analysis: string;
  specMin: number | null;
  specMax: number | null;
  specMinDisplay: string;
  specMaxDisplay: string;
  specUnit: string | null;
  testValue: number | null;
  testValueRaw: string;
  units: string;
  passed: boolean;
  reason?: string;
}

const toShortText = (value: string, maxLength = 10): string => {
  const text = String(value || '');
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const mapCompareDtosToRows = (batchNumber: string, compareItems: CompareRowDto[]): ComparisonResult[] =>
  compareItems.map((item) => {
    const testValueRaw = String(item.testValue || '');
    const unitAlignment = convertTestValueRawToUnit(testValueRaw, item.units || '', item.uom || '');
    const evaluation = evaluateTestValueAgainstSpec(unitAlignment.valueRaw, item.specMin, item.specMax);
    return {
      batchNumber: item.batchNumber || batchNumber,
      parameter: item.parameter || '-',
      testMethod: item.testMethod || '-',
      uom: item.uom || '-',
      analysis: item.analysis || '-',
      specMin: toNumber(item.specMin),
      specMax: toNumber(item.specMax),
      specMinDisplay: String(item.specMin || ''),
      specMaxDisplay: String(item.specMax || ''),
      specUnit: item.uom || null,
      testValue: evaluation.numericValue,
      testValueRaw: unitAlignment.valueRaw,
      units: unitAlignment.unit || item.units || '',
      passed: evaluation.passed,
      reason: evaluation.passed ? '' : evaluation.reason,
    };
  });

interface Step3PanelProps {
  selectedBatch: string;
  isViewMode: boolean;
  isRetestMode: boolean;
  taskId?: string | null;
  sourceTaskId?: string | null;
  initialResults: ComparisonResult[];
  onBack: () => void;
  onSave: (task: ReleaseTask, results: ComparisonResult[]) => void;
  onLoadingChange: (loading: boolean) => void;
  onErrorChange: (error: string) => void;
}

export default function Step3Panel({
  selectedBatch,
  isViewMode,
  isRetestMode,
  sourceTaskId,
  initialResults,
  onBack,
  onSave,
  onLoadingChange,
  onErrorChange,
}: Step3PanelProps) {
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [savingTask, setSavingTask] = useState(false);

  const passCount = useMemo(
    () => comparisonResults.filter((item) => item.passed).length,
    [comparisonResults]
  );

  const fetchCompareRowsByBatch = useCallback(async (batchNumber: string): Promise<ComparisonResult[]> => {
    const compareItems = await getReleaseCompareRowsByBatch(batchNumber);
    return mapCompareDtosToRows(batchNumber, compareItems);
  }, []);

  const clearCompareRowsForBatch = useCallback(async (batchNumber: string) => {
    const existingRows = await getReleaseCompareRowsByBatch(batchNumber);
    if (existingRows.length > 0) {
      await deleteReleaseCompareRowsByBatch(
        existingRows.map((item) => Number(item.id)).filter((id) => Number.isFinite(id))
      );
    }
  }, []);

  const saveCompareRowsForBatch = useCallback(
    async (batchNumber: string, rows: ComparisonResult[]) => {
      await clearCompareRowsForBatch(batchNumber);
      const payloadItems = rows.map((item) => ({
        BatchNumber: batchNumber,
        Parameter: item.parameter,
        TestMethod: item.testMethod || '',
        Uom: item.uom || '',
        Analysis: item.analysis || '',
        Units: item.units || '',
        SpecMin: item.specMinDisplay || '',
        SpecMax: item.specMaxDisplay || '',
        TestValue: item.testValueRaw || String(item.testValue ?? ''),
        TestResult: item.passed ? 'passed' : 'failed',
      }));
      await createReleaseCompareRowsByBatch(payloadItems);
    },
    [clearCompareRowsForBatch]
  );

  // Load data in view mode
  useEffect(() => {
    if (!isViewMode || !selectedBatch) return;
    const load = async () => {
      onLoadingChange(true);
      onErrorChange('');
      try {
        const rows = await fetchCompareRowsByBatch(selectedBatch);
        setComparisonResults(rows);
      } catch (error) {
        onErrorChange(error instanceof Error ? error.message : 'Load view data failed.');
      } finally {
        onLoadingChange(false);
      }
    };
    void load();
  }, [isViewMode, selectedBatch, fetchCompareRowsByBatch, onLoadingChange, onErrorChange]);

  // Sync initialResults when coming from Step 2
  useEffect(() => {
    if (!isViewMode) {
      setComparisonResults(initialResults);
    }
  }, [isViewMode, initialResults]);

  const handleSave = useCallback(() => {
    const saveTask = async () => {
      const batchNumber = selectedBatch.trim();
      if (!isRetestMode && !batchNumber) throw new Error('Batch Number is required.');
      if (comparisonResults.length === 0) throw new Error('No comparison rows to save.');

      const allPassed = comparisonResults.every((item) => item.passed);

      if (!isRetestMode) {
        const existing = await getReleaseTasks({
          range: '[0,9999]',
          perPage: '10000',
          filter: JSON.stringify({ batchNumber }),
        });
        const found = existing.find(
          (item) => normalizeComparableText(String(item.batchNumber || '')) === normalizeComparableText(batchNumber)
        );
        if (found) throw new Error(`Batch ${batchNumber} already has an existing release task.`);
      }

      await saveCompareRowsForBatch(batchNumber, comparisonResults);
      const savedCompareRows = await fetchCompareRowsByBatch(batchNumber);

      let saved: any;
      if (isRetestMode && sourceTaskId) {
        saved = await updateReleaseTask(sourceTaskId, {
          BatchNumber: batchNumber,
          Result: allPassed ? 'passed' : 'failed',
        });
      } else {
        saved = await createReleaseTask({ BatchNumber: batchNumber, Result: allPassed ? 'passed' : 'failed' });
      }

      const taskSource = saved?.task ?? saved ?? {};
      const newTask: ReleaseTask = {
        id: String(taskSource?.id ?? taskSource?.Id ?? sourceTaskId ?? Date.now().toString()),
        batchNumber,
        status: allPassed ? 'passed' : 'failed',
        result: allPassed ? 'passed' : 'failed',
        createdBy: String(taskSource?.createdBy ?? taskSource?.CreatedBy ?? '-'),
        createdAt: String(taskSource?.createdAt ?? taskSource?.CreatedAt ?? new Date().toISOString()),
        updatedAt: String(taskSource?.updatedAt ?? taskSource?.UpdatedAt ?? new Date().toISOString()),
        totalItems: savedCompareRows.length,
        passedItems: savedCompareRows.filter((item) => item.passed).length,
      };

      setComparisonResults(savedCompareRows);
      onSave(newTask, savedCompareRows);
    };

    setSavingTask(true);
    saveTask()
      .catch((error) => {
        onErrorChange(error instanceof Error ? error.message : 'Save failed.');
      })
      .finally(() => setSavingTask(false));
  }, [
    selectedBatch,
    isRetestMode,
    sourceTaskId,
    comparisonResults,
    saveCompareRowsForBatch,
    fetchCompareRowsByBatch,
    onSave,
    onErrorChange,
  ]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <h3 className="text-lg">Step 3: Release result</h3>
          <p className="text-sm text-gray-500">batch number:{selectedBatch || '-'}</p>
        </div>
        <div className="flex items-center gap-2">
          <ReleaseTaskExport
            batchNumber={selectedBatch}
            comparisonResults={comparisonResults}
            disabled={savingTask || comparisonResults.length === 0}
          />
          {!isViewMode && (
            <>
              <Button variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="button" disabled={savingTask} onClick={handleSave}>
                <Save className="size-4 mr-2" />
                {savingTask ? 'Saving...' : 'Save Release Task'}
              </Button>
            </>
          )}
          {comparisonResults.every((item) => item.passed) ? (
            <>
              <CheckCircle2 className="size-5 text-green-600" />
              <span className="text-green-600 text-sm">All Passed</span>
            </>
          ) : (
            <>
              <XCircle className="size-5 text-red-600" />
              <span className="text-red-600 text-sm">Has Failed Items</span>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
          <div className="text-xl">{comparisonResults.length}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-center">
          <div className="text-xl text-green-700">{passCount}</div>
          <div className="text-xs text-green-700">Pass</div>
        </div>
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-center">
          <div className="text-xl text-red-700">{comparisonResults.length - passCount}</div>
          <div className="text-xs text-red-700">Failed</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full table-fixed text-xs md:text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-left whitespace-normal break-words">Parameter</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">Test Method</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">UOM</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">Analysis</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">Unit</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">Spec Min</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">Spec Max</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">TestValue</th>
              <th className="px-3 py-2 text-left whitespace-normal break-words">TestResult</th>
            </tr>
          </thead>
          <tbody>
            {comparisonResults.map((item, index) => (
              <tr
                key={`${item.parameter}-${index}`}
                className={item.passed ? 'bg-green-50 border-b border-gray-100' : 'bg-red-50 border-b border-gray-100'}
              >
                <td className="px-3 py-2 align-top whitespace-normal break-words">{item.parameter}</td>
                <td className="px-3 py-2 align-top whitespace-normal break-words">{item.testMethod || '-'}</td>
                <td className="px-3 py-2 align-top whitespace-normal break-words">{item.uom || '-'}</td>
                <td className="px-3 py-2 align-top whitespace-normal break-words">{item.analysis || '-'}</td>
                <td className="px-3 py-2 align-top whitespace-normal break-words">{item.units || '-'}</td>
                <td className="px-3 py-2 align-top whitespace-normal break-words" title={item.specMinDisplay || '-'}>
                  {item.specMinDisplay ? toShortText(item.specMinDisplay, 30) : '-'}
                </td>
                <td className="px-3 py-2 align-top whitespace-normal break-words" title={item.specMaxDisplay || '-'}>
                  {item.specMaxDisplay ? toShortText(item.specMaxDisplay, 30) : '-'}
                </td>
                <td className="px-3 py-2 align-top whitespace-normal break-words">{item.testValueRaw || '-'}</td>
                <td className="px-3 py-2 align-top whitespace-normal break-words">
                  {item.passed ? (
                    <span className="inline-flex items-center gap-1 text-green-700">
                      <CheckCircle2 className="size-4" />
                      Pass
                    </span>
                  ) : (
                    <div className="text-red-700">
                      <span className="inline-flex items-center gap-1">
                        <XCircle className="size-4" />
                        Failed
                      </span>
                      {item.reason && (
                        <div className="text-xs mt-0.5 max-w-xs" title={item.reason}>
                          ({toShortText(item.reason, 50)})
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
